import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import InternalNavbar from '../../components/InternalNavbar';
import Navbar from '../../components/header';
import LevelTestModal from '../../components/LevelTestModal';
import { toast } from 'react-toastify';
import { FaSpinner } from 'react-icons/fa';
import api from '../../services/api';
import assessmentService from '../../services/assessmentService';
import './iframe-test.css';

const IframeLevelTest = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { trackAssessmentStarted, trackAssessmentCompleted, trackError } = useAnalytics();
    const iframeRef = useRef(null);

    const [iframeLoading, setIframeLoading] = useState(true);
    const [iframeError, setIframeError] = useState(false);
    const [clReady, setClReady] = useState(false);
    const [accessToken, setAccessToken] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(true);
    const [existingResult, setExistingResult] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [checkingHistory, setCheckingHistory] = useState(true);

    // Content Layer assessment URL
    const CONTENT_BASE_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.anasarabic.com';
    const ASSESSMENT_PATH = '/assessment/%D8%A7%D8%AE%D8%AA%D8%A8%D8%A7%D8%B1_%D8%AA%D8%AD%D8%AF%D9%8A%D8%AF_%D8%A7%D9%84%D9%85%D8%B3%D8%AA%D9%88%D9%89_%D9%81%D9%8A_%D8%A7%D9%84%D9%84%D8%BA%D8%A9_%D8%A7%D9%84%D8%B9%D8%B1%D8%A8%D9%8A%D8%A9_%D9%84%D9%84%D9%86%D8%A7%D8%B7%D9%82%D9%8A%D9%86_%D8%A8%D8%BA%D9%8A%D8%B1%D9%87%D8%A7';

    // Build assessment URL with token (if available)
    const ASSESSMENT_URL = accessToken
        ? `${CONTENT_BASE_URL}${ASSESSMENT_PATH}?token=${accessToken}`
        : `${CONTENT_BASE_URL}${ASSESSMENT_PATH}`;

    // Dynamic navbar based on auth
    const NavComponent = isAuthenticated ? InternalNavbar : Navbar;

    // Check if authenticated user has already completed the level test
    useEffect(() => {
        const checkTestHistory = async () => {
            // Guests don't have test history - skip check
            if (!isAuthenticated || !user) {
                setCheckingHistory(false);
                setTokenLoading(false);
                return;
            }

            try {
                setCheckingHistory(true);

                console.log('[LevelTest] Checking test history for user:', user.email);
                const history = await assessmentService.getTestHistory();
                console.log('[LevelTest] Test history received:', history);

                // Handle both response formats: array or object with tests property
                const tests = Array.isArray(history) ? history : (history?.tests || []);
                console.log('[LevelTest] Parsed tests array:', tests);

                // Check if user has any completed assessments
                if (tests && tests.length > 0) {
                    // Get the most recent test result
                    const mostRecentTest = tests[0];
                    console.log('[LevelTest] Found existing test result:', mostRecentTest);
                    setExistingResult(mostRecentTest);
                    setShowModal(true);
                } else {
                    console.log('[LevelTest] No existing test results found - user can take test');
                }
            } catch (error) {
                // If error fetching history, allow user to proceed
                console.error('[LevelTest] Error checking test history:', error);
            } finally {
                setCheckingHistory(false);
            }
        };

        checkTestHistory();
    }, [isAuthenticated, user]);

    // Fetch or create token for assessment access
    useEffect(() => {
        const getAccessToken = async () => {
            // Skip if we have existing result (history check found one)
            if (existingResult) {
                return;
            }

            try {
                setTokenLoading(true);

                if (isAuthenticated && user) {
                    // Authenticated user - use existing flow
                    const response = await api.post('/content/generate-token', {
                        content_id: 'level_test_assessment',
                        content_type: 'assessment'
                    });
                    setAccessToken(response.data.access_token);
                } else {
                    // Guest user - create guest account with attribution
                    const anonymousId = localStorage.getItem('op_anonymous_id') ||
                        `anon_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;

                    // Collect attribution data from sessionStorage
                    let attribution = {};
                    try {
                        const utmParams = sessionStorage.getItem('utm_params');
                        const adClickIds = sessionStorage.getItem('ad_click_ids');
                        if (utmParams) attribution = { ...attribution, ...JSON.parse(utmParams) };
                        if (adClickIds) attribution = { ...attribution, ...JSON.parse(adClickIds) };
                    } catch (e) {
                        console.warn('[LevelTest] Failed to parse attribution data:', e);
                    }

                    // Collect device info
                    const deviceInfo = {
                        userAgent: navigator.userAgent,
                        language: navigator.language,
                        screenWidth: window.screen.width,
                        screenHeight: window.screen.height,
                        referrer: document.referrer || null,
                    };

                    // Create guest account
                    const response = await api.post('/auth/create-guest', {
                        anonymous_id: anonymousId,
                        attribution: attribution,
                        device_info: deviceInfo,
                    });

                    // Store guest_id for later upgrade during signup
                    localStorage.setItem('guest_id', response.data.guest_id);
                    setAccessToken(response.data.content_token);

                    console.log('[LevelTest] Created guest account:', response.data.guest_id);
                }
            } catch (error) {
                console.error('[LevelTest] Error getting access token:', error);
                // If token fails for guests, we can't proceed
                if (!isAuthenticated) {
                    toast.error('Failed to initialize assessment. Please try again.');
                }
                setAccessToken(null);
            } finally {
                setTokenLoading(false);
            }
        };

        getAccessToken();
    }, [isAuthenticated, user, existingResult]);

    // Handle iframe load (HTML loaded, but app may not be ready yet)
    const handleIframeLoad = () => {
        // Don't hide loading yet - wait for CL_READY event
        setIframeError(false);
    };

    // Handle iframe error
    const handleIframeError = () => {
        setIframeLoading(false);
        setIframeError(true);
        toast.error('Failed to load assessment. Please try again.');
    };

    // Listen for messages from the iframe
    useEffect(() => {
        const handleMessage = (event) => {
            // Verify origin for security
            const allowedOrigins = [
                'https://content.anasarabic.com',
                'http://localhost:5173',  // CL dev server (Vite)
                'http://localhost:5000',
                'http://localhost:3001'
            ];

            if (!allowedOrigins.includes(event.origin)) {
                return;
            }

            // Handle different message types
            switch (event.data.type) {
                case 'CL_READY':
                    // CL app is fully initialized and ready
                    setIframeLoading(false);
                    setClReady(true);

                    // Track assessment started
                    trackAssessmentStarted('level_test');

                    toast.success('Assessment loaded successfully!', {
                        autoClose: 2000,
                        position: 'top-center'
                    });
                    break;

                case 'ASSESSMENT_COMPLETE':
                    // Assessment completed - save results and navigate

                    // Validate message structure
                    if (!event.data.data) {
                        toast.error('Invalid assessment data received');
                        return;
                    }

                    handleAssessmentComplete(event.data.data);
                    break;

                case 'ASSESSMENT_PROGRESS':
                    // Progress update (future implementation)
                    break;

                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [CONTENT_BASE_URL, navigate, isAuthenticated, user, accessToken]);

    // Handle assessment completion from CL
    const handleAssessmentComplete = async (resultData) => {
        try {
            // Validate required fields
            if (!resultData.level || typeof resultData.score !== 'number' ||
                typeof resultData.total_questions !== 'number' ||
                typeof resultData.correct_answers !== 'number') {
                throw new Error('Invalid assessment data: missing required fields');
            }

            // Validate score range
            if (resultData.score < 0 || resultData.score > 100) {
                throw new Error('Invalid score: must be between 0 and 100');
            }

            // Validate level values
            const validLevels = ['Beginner', 'Elementary', 'Intermediate', 'Advanced'];
            if (!validLevels.includes(resultData.level)) {
                throw new Error(`Invalid level: must be one of ${validLevels.join(', ')}`);
            }

            // GUEST HANDLING: Save results to backend, then redirect to signup
            if (!isAuthenticated) {
                console.log('[LevelTest] Guest completed test, saving results and redirecting to signup');

                // Store results in sessionStorage as backup
                sessionStorage.setItem('guest_test_results', JSON.stringify({
                    ...resultData,
                    completed_at: new Date().toISOString()
                }));

                // Save results to backend for the guest user
                try {
                    const guestId = localStorage.getItem('guest_id');
                    if (guestId && accessToken) {
                        console.log('[LevelTest] Saving guest test results to backend');
                        // Use the specific guest endpoint that accepts content tokens
                        await api.post('/content/save-guest-assessment-results', {
                            level: resultData.level,
                            score: resultData.score,
                            total_questions: resultData.total_questions,
                            correct_answers: resultData.correct_answers,
                            time_taken: resultData.time_taken || 0,
                            assessment_data: resultData.assessment_data || {}
                        }, {
                            headers: {
                                'Authorization': `Bearer ${accessToken}`
                            }
                        });
                        console.log('[LevelTest] Guest test results saved successfully');
                    } else {
                        console.warn('[LevelTest] No guest_id or accessToken - cannot save results to backend');
                    }
                } catch (saveError) {
                    console.error('[LevelTest] Failed to save guest results to backend:', saveError);
                    // Continue with redirect even if save fails - results are in sessionStorage
                }

                // Navigate to signup with results
                setTimeout(() => {
                    navigate('/signup', {
                        state: {
                            fromGuestLevelTest: true,
                            testResults: {
                                level: resultData.level,
                                score: resultData.score,
                                total_questions: resultData.total_questions,
                                correct_answers: resultData.correct_answers,
                                time_taken: resultData.time_taken,
                                completed_at: new Date().toISOString()
                            }
                        }
                    });
                }, 1000);
                return;
            }

            // AUTHENTICATED HANDLING: Save results to BL backend
            const response = await api.post('/content/save-assessment-results', {
                level: resultData.level,
                score: resultData.score,
                total_questions: resultData.total_questions,
                correct_answers: resultData.correct_answers,
                time_taken: resultData.time_taken || null,
                assessment_data: resultData.assessment_data || null
            });

            // Track assessment completion
            trackAssessmentCompleted({
                assessmentType: 'level_test',
                score: resultData.score,
                level: resultData.level,
                timeSpent: resultData.time_taken || 0,
            });

            // Show success message
            toast.success('Assessment completed successfully!', {
                autoClose: 3000
            });

            // Navigate to results page with the saved result ID
            setTimeout(() => {
                navigate('/level-test/results', {
                    state: {
                        testResults: {
                            id: response.data.id,
                            level_result: response.data.level,
                            score: response.data.score,
                            total_questions: response.data.total_questions,
                            correct_answers: response.data.correct_answers,
                            time_taken: response.data.time_taken,
                            test_date: response.data.completed_at,
                            created_at: response.data.completed_at
                        },
                        fromTest: true
                    }
                });
            }, 1500);

        } catch (error) {
            console.error('Error saving assessment results:', error);

            // Track error
            trackError('assessment_save_failed', error.message, {
                assessment_type: 'level_test',
                score: resultData.score,
                level: resultData.level,
            });

            // Show error but still show success for user experience
            toast.error('Assessment completed, but failed to save results. Please contact support.');

            // Still navigate to results page with data from CL
            setTimeout(() => {
                navigate('/level-test/results', {
                    state: {
                        testResults: {
                            id: 'temp-' + Date.now(),
                            level_result: resultData.level,
                            score: resultData.score,
                            total_questions: resultData.total_questions,
                            correct_answers: resultData.correct_answers,
                            time_taken: resultData.time_taken,
                            test_date: new Date().toISOString(),
                            created_at: new Date().toISOString(),
                            error: 'Failed to save to database'
                        },
                        fromTest: true
                    }
                });
            }, 1500);
        }
    };

    return (
        <div className="level-test-page iframe-test-page">
            <NavComponent />

            {/* Level Test Restriction Modal - Cannot be dismissed if user has taken test */}
            {existingResult && (
                <LevelTestModal
                    isOpen={true}
                    onClose={() => {/* Modal cannot be closed - user must navigate away */ }}
                    existingResult={existingResult}
                />
            )}

            {/* Loading overlay - show while checking history, fetching token or loading iframe */}
            {(checkingHistory || tokenLoading || (iframeLoading && !clReady)) && (
                <div className="iframe-loading">
                    <FaSpinner className="spinner" />
                    <p>
                        {checkingHistory ? 'Checking test history...' : tokenLoading ? 'Authenticating...' : 'Loading assessment...'}
                    </p>
                    <small>
                        {checkingHistory
                            ? 'Please wait...'
                            : tokenLoading
                                ? 'Verifying your access...'
                                : 'Waiting for content to initialize...'}
                    </small>
                </div>
            )}

            {/* Error overlay */}
            {iframeError && (
                <div className="iframe-error">
                    <p>Failed to load assessment. Please refresh the page or try again later.</p>
                    <button
                        className="btn-retry"
                        onClick={() => {
                            setIframeError(false);
                            setIframeLoading(true);
                            if (iframeRef.current) {
                                iframeRef.current.src = ASSESSMENT_URL;
                            }
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Iframe Content Section - Render for guests (no token) or authenticated users (with token) */}
            {!checkingHistory && !existingResult && !tokenLoading && (
                <div className="level-test-content-section">
                    <iframe
                        ref={iframeRef}
                        src={ASSESSMENT_URL}
                        title="Arabic Level Assessment"
                        className={`assessment-iframe ${iframeLoading ? 'loading' : ''}`}
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                        allow="fullscreen"
                        allowFullScreen
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    />
                </div>
            )}

        </div>
    );
};

export default IframeLevelTest;
